import React from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Container, Box, Snackbar, Alert } from '@mui/material';
import {
  People as PeopleIcon,
  Business as BusinessIcon,
  Build as BuildIcon,
  Description as ContractsIcon,
  Assignment as InstancesIcon,
  AttachMoney as CostsIcon,
  Category as GroupsIcon,
  Assessment as ReportsIcon,
  CameraRoll as CalculatorIcon,
  AccountBalance as BudgetIcon,
  Assessment as AssessmentIcon,
  AccountBalanceWallet as BankingIcon,
  TrendingUp as LeadsIcon,
} from '@mui/icons-material';

import Login from './components/Login';
import Signup from './components/Signup';
import Dashboard from './components/Dashboard';
import Layout from './components/Layout';
import PlaceholderPage from './components/PlaceholderPage';
import Teams from './components/Teams';
import Entities from './components/Entities';
import Contacts from './components/Contacts';

import Proposals from './components/Proposals';
import Subscriptions from './components/Subscriptions';
import Contracts from './components/Contracts';
import Clients from './components/Clients';
import Finance from './components/Finance';
// Legacy finance modules removed in favor of Ledger and Bank Management
import Ledger from './components/Ledger';
import BankManagement from './components/BankManagement';
import ClientBalances from './components/ClientBalances';
import AccountsReceivable from './components/AccountsReceivable';
import AccountsPayable from './components/AccountsPayable';
import AppUsers from './components/AppUsers';
import AppConfig from './components/AppConfig';
import Leads from './components/Leads';
import Costs from './components/Costs';
import Tasks from './components/tasks/Tasks';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ConfigProvider } from './contexts/ConfigContext';
import DynamicThemeProvider from './contexts/ThemeContext';
import useDynamicBranding from './hooks/useDynamicBranding';
import PublicProposal from './components/PublicProposal';
import PublicContract from './components/PublicContract';
import PublicVendorLedger from './components/PublicVendorLedger';

function App() {
  return (
    <AuthProvider>
      <ConfigProvider>
        <DynamicThemeProvider>
          <AppContent />
        </DynamicThemeProvider>
      </ConfigProvider>
    </AuthProvider>
  );
}

// Login page; once signed in, returns to the page that sent the user here
function LoginRoute({ children }) {
  const { currentUser } = useAuth();
  const location = useLocation();
  if (currentUser) {
    return <Navigate to={location.state?.from || '/dashboard'} replace />;
  }
  return children;
}

// Shared links that only signed-in users may open (no app layout)
function RequireLogin({ children }) {
  const { currentUser } = useAuth();
  const location = useLocation();
  if (!currentUser) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return children;
}

function AppContent() {
  const { currentUser } = useAuth();
  
  // Apply dynamic branding to document
  useDynamicBranding();

  // Auth Layout for login/signup pages
  const AuthLayout = ({ children }) => (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {children}
      </Box>
    </Container>
  );

  // Protected Route wrapper
  const ProtectedRoute = ({ children, requireSA = false, requireFinance = false }) => {
    const { currentUser, userProfile, isSuperAdmin, canViewFinance } = useAuth();
    
    const RedirectWithNotice = ({ to, message }) => {
      const [open, setOpen] = React.useState(true);
      const navigate = useNavigate();
      React.useEffect(() => {
        const id = setTimeout(() => navigate(to, { replace: true }), 1500);
        return () => clearTimeout(id);
      }, [navigate, to]);
      return (
        <>
          <Snackbar
            open={open}
            autoHideDuration={1500}
            onClose={() => setOpen(false)}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert onClose={() => setOpen(false)} severity="warning" sx={{ width: '100%' }}>
              {message}
            </Alert>
          </Snackbar>
        </>
      );
    };
    
    if (!currentUser) {
      return (
        <RedirectWithNotice 
          to="/login" 
          message="Your session has expired or you are not signed in. Redirecting to login..."
        />
      );
    }
    
    // If SA access is required but user is not SA, redirect to dashboard
    if (requireSA && !isSuperAdmin()) {
      return <Navigate to="/dashboard" replace />;
    }

    // Members have no access to the Finance section
    if (requireFinance && !canViewFinance()) {
      return <Navigate to="/dashboard" replace />;
    }
    
    return <Layout>{children}</Layout>;
  };

  return (
    <Routes>
      {/* Auth Routes */}
      <Route 
        path="/login" 
        element={
          <LoginRoute>
            <AuthLayout>
              <Login />
            </AuthLayout>
          </LoginRoute>
        } 
      />
   
  

      {/* Protected Routes */}
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } 
      />

      {/* Management Routes */}
      <Route
        path="/subscriptions"
        element={
          <ProtectedRoute>
            <Subscriptions />
          </ProtectedRoute>
        }
      />

      <Route 
        path="/proposals" 
        element={
          <ProtectedRoute>
            <Proposals />
          </ProtectedRoute>
        } 
      />
      
      <Route
        path="/clients"
        element={
          <ProtectedRoute>
            <Clients />
          </ProtectedRoute>
        }
      />

      <Route
        path="/tasks"
        element={
          <ProtectedRoute>
            <Tasks />
          </ProtectedRoute>
        }
      />

      <Route 
        path="/suppliers" 
        element={
          <ProtectedRoute>
            <PlaceholderPage 
              title="Suppliers"
              description="Manage supplier relationships and vendor information."
              icon={<BusinessIcon sx={{ fontSize: 40 }} />}
              color="#388e3c"
              features={[
                'Supplier Directory',
                'Vendor Management',
                'Performance Tracking',
                'Contract Management',
                'Payment Terms',
                'Quality Assessment'
              ]}
            />
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/entities" 
        element={
          <ProtectedRoute>
            <Entities />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/teams" 
        element={
          <ProtectedRoute>
            <Teams />
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/contacts" 
        element={
          <ProtectedRoute>
            <Contacts />
          </ProtectedRoute>
        } 
      />



      <Route 
        path="/service-groups" 
        element={
          <ProtectedRoute>
            <PlaceholderPage 
              title="Service Groups"
              description="Organize services into logical groups and categories."
              icon={<GroupsIcon sx={{ fontSize: 40 }} />}
              color="#7b1fa2"
              features={[
                'Group Management',
                'Service Categorization',
                'Bulk Operations',
                'Group Analytics',
                'Template Creation',
                'Hierarchy Management'
              ]}
            />
          </ProtectedRoute>
        } 
      />

      <Route
        path="/contracts"
        element={
          <ProtectedRoute>
            <Contracts />
          </ProtectedRoute>
        }
      />

      <Route
        path="/contracts/:contractId"
        element={
          <ProtectedRoute>
            <Contracts />
          </ProtectedRoute>
        }
      />

      <Route
        path="/contract-instances"
        element={
          <ProtectedRoute>
            <PlaceholderPage 
              title="Contract Instances"
              description="Track active contract instances and their execution."
              icon={<InstancesIcon sx={{ fontSize: 40 }} />}
              color="#ed6c02"
              features={[
                'Instance Tracking',
                'Execution Monitor',
                'Status Updates',
                'Timeline Management',
                'Resource Allocation',
                'Progress Reports'
              ]}
            />
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/costs" 
        element={
          <ProtectedRoute>
            <Costs />
          </ProtectedRoute>
        } 
      />

      {/* Finance Route */}
      <Route 
        path="/finance" 
        element={
          <ProtectedRoute requireFinance>
            <Finance />
          </ProtectedRoute>
        } 
      />

      {/* AR/AP legacy routes removed */}

      {/* Configuration Routes (Super Admin Only) */}
      <Route 
        path="/app-users" 
        element={
          <ProtectedRoute requireSA={true}>
            <AppUsers />
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/app-config" 
        element={
          <ProtectedRoute requireSA={true}>
            <AppConfig />
          </ProtectedRoute>
        } 
      />

      {/* Additional missing routes */}
      <Route 
        path="/leads" 
        element={
          <ProtectedRoute>
            <Leads />
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/bank-management" 
        element={
          <ProtectedRoute requireFinance>
            <BankManagement />
          </ProtectedRoute>
        } 
      />

      <Route
        path="/client-balances"
        element={
          <ProtectedRoute requireFinance>
            <ClientBalances />
          </ProtectedRoute>
        }
      />

      <Route 
        path="/accounts-receivable" 
        element={
          <ProtectedRoute requireFinance>
            <AccountsReceivable />
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/ledger" 
        element={
          <ProtectedRoute requireFinance>
            <Ledger />
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/accounts-payable" 
        element={
          <ProtectedRoute requireFinance>
            <AccountsPayable />
          </ProtectedRoute>
        } 
      />

      {/* Default Route */}
      <Route path="/" element={<Navigate to={currentUser ? "/dashboard" : "/login"} replace />} />

      {/* Public Share Routes (no auth) */}
      <Route path="/p/:proposalId/:token" element={<RequireLogin><PublicProposal /></RequireLogin>} />
      <Route path="/c/:contractId/:token" element={<PublicContract />} />
      <Route path="/v/:entityId/:token" element={<PublicVendorLedger />} />
    </Routes>
  );
}

export default App;
